import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiImage, FiZap, FiDownload, FiRefreshCw, FiCheck, FiX, FiTrash2, FiClock, FiDollarSign, FiPlay, FiLayers, FiEdit3, FiStopCircle, FiArrowUp, FiArrowDown, FiUpload, FiSettings, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../services/api';

// Note: This component is embedded in the NFTs page as a tab

const AICartoonify = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('batch');

  // Single test state
  const [imageUrl, setImageUrl] = useState('');
  const [selectedModel, setSelectedModel] = useState('flux-edit');
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [models, setModels] = useState([]);
  const [sampleImages, setSampleImages] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Batch state
  const [batchChallenges, setBatchChallenges] = useState([]);
  const [batchStatus, setBatchStatus] = useState({ status: 'idle' });
  const [batchPrompt, setBatchPrompt] = useState('');
  const [batchModel, setBatchModel] = useState('flux-edit');
  const [batchLimit, setBatchLimit] = useState(10);
  const [batchRarity, setBatchRarity] = useState('common');
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [rarityStats, setRarityStats] = useState({});
  const [sortBy, setSortBy] = useState('date'); // 'date' or 'name'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Retry modal state
  const [retryModal, setRetryModal] = useState(null);
  const [retryPrompt, setRetryPrompt] = useState('');
  const [retryModel, setRetryModel] = useState('flux-edit');
  const [retryRarity, setRetryRarity] = useState('common');
  const [retrying, setRetrying] = useState(false);
  const [rarityPrompts, setRarityPrompts] = useState({});
  const [savingPrompt, setSavingPrompt] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    autoGenerate: true,
    defaultModel: 'hayao',
    rarityPrompts: { common: '', rare: '', epic: '', legendary: '' }
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Rarity options
  const rarities = [
    { id: 'common', name: 'Common', color: 'gray' },
    { id: 'rare', name: 'Rare', color: 'blue' },
    { id: 'epic', name: 'Epic', color: 'purple' },
    { id: 'legendary', name: 'Legendary', color: 'yellow' }
  ];

  // Load available models, sample images, and saved gallery
  useEffect(() => {
    const loadData = async () => {
      try {
        const [modelsRes, samplesRes, galleryRes, settingsRes] = await Promise.all([
          api.get('/cartoonify/models'),
          api.get('/cartoonify/sample-images'),
          api.get('/cartoonify/gallery'),
          api.get('/cartoonify/settings')
        ]);
        setModels(modelsRes.data.models);
        setSampleImages(samplesRes.data.images || []);

        // Load settings (includes rarity prompts)
        if (settingsRes.data.success) {
          setSettings(settingsRes.data.settings);
          setAvailableModels(settingsRes.data.availableModels || []);
          // Also update legacy rarityPrompts state for backward compatibility
          if (settingsRes.data.settings.rarityPrompts) {
            setRarityPrompts(settingsRes.data.settings.rarityPrompts);
          }
        }

        // Set default prompt from flux-edit model
        const fluxEdit = modelsRes.data.models.find(m => m.id === 'flux-edit');
        if (fluxEdit?.defaultPrompt) {
          setPrompt(fluxEdit.defaultPrompt);
          setBatchPrompt(fluxEdit.defaultPrompt);
        }

        // Load saved gallery images
        if (galleryRes.data.images) {
          const savedImages = galleryRes.data.images.map((img, idx) => ({
            id: `saved_${idx}`,
            cartoonUrl: img.url,
            modelKey: img.modelKey || 'unknown',
            modelName: img.modelKey || 'Unknown',
            modelType: img.source === 'batch' ? 'batch' : 'test',
            filename: img.filename,
            duration: null,
            cost: null,
            prompt: null,
            saved: true
          }));
          setGallery(savedImages);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (retryModal) {
          setRetryModal(null);
        } else if (enlargedImage) {
          setEnlargedImage(null);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [retryModal, enlargedImage]);

  // Load batch challenges on initial mount and when switching to batch tab
  useEffect(() => {
    loadBatchChallenges();
  // eslint-disable-next-line
  }, []);

  // Load batch challenges when switching to batch tab
  const loadBatchChallenges = useCallback(async () => {
    setLoadingChallenges(true);
    try {
      const response = await api.get('/cartoonify/challenges/all');
      setBatchChallenges(response.data.challenges || []);
      setRarityStats(response.data.rarityStats || {});
    } catch (error) {
      console.error('Failed to load challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoadingChallenges(false);
    }
  }, []);

  // Load batch status periodically when running
  useEffect(() => {
    let interval;
    if (batchStatus.status === 'running') {
      interval = setInterval(async () => {
        try {
          const response = await api.get('/cartoonify/batch/status');
          setBatchStatus(response.data);
          if (response.data.status === 'completed') {
            toast.success(`Batch completed: ${response.data.completed} success, ${response.data.failed} failed`);
            loadBatchChallenges();
          }
        } catch (error) {
          console.error('Failed to get batch status:', error);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [batchStatus.status, loadBatchChallenges]);

  // Get current model info
  const currentModel = models.find(m => m.id === selectedModel) || {};

  // Test connection
  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await api.post('/cartoonify/test-connection', {}, { timeout: 120000 });
      setConnectionStatus(response.data);
      if (response.data.success) {
        toast.success('Replicate API connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: error.message });
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  // Handle image upload for single test
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only image files (JPEG, PNG, GIF, WebP) are allowed');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await api.post('/cartoonify/upload-test-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Use the full URL for the API (Replicate needs absolute URLs)
        setImageUrl(response.data.fullUrl);
        toast.success('Image uploaded successfully');
      } else {
        toast.error(response.data.error || 'Failed to upload image');
        setUploadedImagePreview(null);
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
      setUploadedImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  // Clear uploaded image
  const clearUploadedImage = () => {
    setImageUrl('');
    setUploadedImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Process image
  const handleCartoonify = async () => {
    if (!imageUrl) {
      toast.error('Please enter an image URL');
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const requestData = {
        imageUrl: imageUrl,
        modelKey: selectedModel
      };

      if (currentModel.supportsPrompt && prompt.trim()) {
        requestData.prompt = prompt.trim();
      }

      const response = await api.post('/cartoonify/single', requestData, {
        timeout: 120000
      });

      if (response.data.success) {
        const newResult = {
          ...response.data,
          timestamp: new Date().toLocaleTimeString(),
          id: Date.now()
        };
        setResult(newResult);
        setGallery(prev => [newResult, ...prev]);
        toast.success(`Cartoonified in ${response.data.duration}s!`);
      } else {
        toast.error(response.data.error || 'Processing failed');
      }
    } catch (error) {
      console.error('Cartoonify error:', error);
      toast.error(error.response?.data?.error || 'Processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // Start batch processing
  const handleStartBatch = async () => {
    // Count challenges missing the selected rarity
    const pending = batchChallenges.filter(c => !c.rarityImages?.[batchRarity]);
    if (pending.length === 0) {
      toast.error(`No challenges missing ${batchRarity} rarity`);
      return;
    }

    try {
      const response = await api.post('/cartoonify/batch/start', {
        modelKey: batchModel,
        prompt: batchPrompt,
        limit: batchLimit,
        rarity: batchRarity
      });

      setBatchStatus({ status: 'running', progress: 0, total: response.data.total, rarity: batchRarity });
      toast.success(`Started batch processing ${response.data.total} challenges (${batchRarity})`);
    } catch (error) {
      console.error('Batch start error:', error);
      toast.error(error.response?.data?.error || 'Failed to start batch');
    }
  };

  // Stop batch processing
  const handleStopBatch = async () => {
    try {
      await api.post('/cartoonify/batch/stop');
      setBatchStatus(prev => ({ ...prev, status: 'stopped' }));
      toast.success('Batch processing stopped');
    } catch (error) {
      toast.error('Failed to stop batch');
    }
  };

  // Retry single challenge
  const handleRetry = async () => {
    if (!retryModal) return;

    setRetrying(true);
    try {
      const response = await api.post(`/cartoonify/challenges/${retryModal.id}/retry`, {
        modelKey: retryModel,
        prompt: retryPrompt,
        rarity: retryRarity
      }, { timeout: 120000 });

      if (response.data.success) {
        toast.success(`${retryRarity.charAt(0).toUpperCase() + retryRarity.slice(1)} image created!`);

        // Reload challenges and update the modal with new data
        const challengesRes = await api.get('/cartoonify/challenges/all');
        setBatchChallenges(challengesRes.data.challenges || []);
        setRarityStats(challengesRes.data.rarityStats || {});

        // Update the modal with refreshed challenge data (keep modal open)
        const updatedChallenge = challengesRes.data.challenges.find(c => c.id === retryModal.id);
        if (updatedChallenge) {
          setRetryModal(updatedChallenge);
        }
      } else {
        toast.error(response.data.error || 'Processing failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  // Delete cartoon from challenge (all rarities)
  const handleDeleteCartoon = async (challengeId) => {
    try {
      await api.delete(`/cartoonify/challenges/${challengeId}/cartoon`);
      toast.success('Cartoon image removed');
      loadBatchChallenges();
    } catch (error) {
      toast.error('Failed to remove cartoon');
    }
  };

  // Delete specific rarity from challenge (used in modal)
  const handleDeleteRarity = async (challengeId, rarity) => {
    try {
      await api.delete(`/cartoonify/challenges/${challengeId}/cartoon?rarity=${rarity}`);
      toast.success(`${rarity.charAt(0).toUpperCase() + rarity.slice(1)} image removed`);
      // Refresh data and update modal
      const challengesRes = await api.get('/cartoonify/challenges/all');
      setBatchChallenges(challengesRes.data.challenges || []);
      setRarityStats(challengesRes.data.rarityStats || {});
      // Update the modal with refreshed data
      const updatedChallenge = challengesRes.data.challenges.find(c => c.id === challengeId);
      if (updatedChallenge) {
        setRetryModal(updatedChallenge);
      }
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  // Save cartoonify settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/cartoonify/settings', {
        autoGenerate: settings.autoGenerate,
        defaultModel: settings.defaultModel,
        rarityPrompts: settings.rarityPrompts
      });
      toast.success('Settings saved successfully');
      // Update legacy rarityPrompts state
      setRarityPrompts(settings.rarityPrompts);
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Save settings error:', error);
    } finally {
      setSavingSettings(false);
    }
  };

  // Update a specific setting
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Update a rarity prompt
  const updateRarityPrompt = (rarity, value) => {
    setSettings(prev => ({
      ...prev,
      rarityPrompts: { ...prev.rarityPrompts, [rarity]: value }
    }));
  };

  // Remove from gallery (local only)
  const removeFromGallery = (id) => {
    setGallery(prev => prev.filter(item => item.id !== id));
  };

  // Delete from server
  const deleteFromServer = async (item) => {
    if (!item.filename) {
      removeFromGallery(item.id);
      return;
    }

    try {
      const source = item.modelType === 'batch' ? 'batch' : 'test';
      await api.delete(`/cartoonify/gallery/${encodeURIComponent(item.filename)}?source=${source}`);
      setGallery(prev => prev.filter(i => i.id !== item.id));
      setEnlargedImage(null);
      toast.success('Image deleted');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete');
    }
  };

  // Save current prompt as default for selected rarity
  const saveRarityPrompt = async () => {
    setSavingPrompt(true);
    try {
      const updatedPrompts = {
        ...rarityPrompts,
        [retryRarity]: retryPrompt
      };
      await api.put('/cartoonify/rarity-prompts', { prompts: updatedPrompts });
      setRarityPrompts(updatedPrompts);
      toast.success(`Saved as default ${retryRarity} prompt`);
    } catch (error) {
      toast.error('Failed to save prompt');
    }
    setSavingPrompt(false);
  };

  // Get prompt for a rarity (saved or model default)
  const getPromptForRarity = (rarity) => {
    if (rarityPrompts[rarity]) {
      return rarityPrompts[rarity];
    }
    // Fall back to model default
    const model = models.find(m => m.id === retryModel);
    return model?.defaultPrompt || '';
  };

  const clearGallery = () => {
    setGallery([]);
    setResult(null);
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
    return `${baseUrl}${path}`;
  };

  // Stats for batch tab
  // Stats based on selected rarity
  const pendingForRarity = batchChallenges.filter(c => !c.rarityImages?.[batchRarity]).length;
  const completedForRarity = batchChallenges.filter(c => c.rarityImages?.[batchRarity]).length;
  // Overall stats - use common rarity as the baseline (challenge_cartoon_images table)
  const pendingCount = batchChallenges.filter(c => !c.rarityImages?.common).length;
  const completedCount = batchChallenges.filter(c => c.rarityImages?.common).length;

  // Sorted challenges for batch grid
  const sortedBatchChallenges = useMemo(() => {
    const sorted = [...batchChallenges].sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortOrder === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else {
        // Sort by date (createdAt)
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return sortOrder === 'asc'
          ? dateA - dateB
          : dateB - dateA;
      }
    });
    return sorted;
  }, [batchChallenges, sortBy, sortOrder]);

  // Toggle sort order or change sort type
  const handleSort = (type) => {
    if (sortBy === type) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(type);
      setSortOrder(type === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Cartoonify</h1>
          <p className="text-gray-600">Convert challenge images to cartoon style for NFTs</p>
        </div>
        <button
          onClick={handleTestConnection}
          disabled={testingConnection}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            connectionStatus?.success
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          {testingConnection ? (
            <FiRefreshCw className="w-4 h-4 animate-spin" />
          ) : connectionStatus?.success ? (
            <FiCheck className="w-4 h-4" />
          ) : (
            <FiZap className="w-4 h-4" />
          )}
          <span>{testingConnection ? 'Testing...' : 'Test Connection'}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('batch');
              loadBatchChallenges();
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'batch'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiLayers className="w-4 h-4 inline mr-2" />
            Batch cartoonify images
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                {pendingCount} pending
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('single')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'single'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiImage className="w-4 h-4 inline mr-2" />
            Single Test
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'gallery'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiImage className="w-4 h-4 inline mr-2" />
            Gallery ({gallery.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiSettings className="w-4 h-4 inline mr-2" />
            Settings
          </button>
        </nav>
      </div>

      {/* Single Test Tab */}
      {activeTab === 'single' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Input</h2>

            {/* Upload Image Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image
              </label>
              {uploadedImagePreview ? (
                <div className="relative">
                  <div className="border-2 border-green-300 rounded-lg overflow-hidden bg-green-50">
                    <div className="flex items-center space-x-3 p-2">
                      <img
                        src={uploadedImagePreview}
                        alt="Uploaded"
                        className="w-20 h-20 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800">Image uploaded</p>
                        <p className="text-xs text-green-600 truncate">{imageUrl}</p>
                      </div>
                      <button
                        onClick={clearUploadedImage}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                        title="Remove image"
                      >
                        <FiX className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="cartoonify-image-upload"
                  />
                  <label
                    htmlFor="cartoonify-image-upload"
                    className={`flex flex-col items-center justify-center w-full py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors ${
                      isUploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <FiRefreshCw className="w-8 h-8 text-primary-500 animate-spin mb-2" />
                        <span className="text-sm text-gray-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm font-medium text-gray-600">Click to upload image</span>
                        <span className="text-xs text-gray-400 mt-1">JPEG, PNG, GIF, WebP up to 10MB</span>
                      </>
                    )}
                  </label>
                </div>
              )}
            </div>

            {/* Sample Images */}
            {sampleImages.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or use Sample Test Images
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {sampleImages.map((sample, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setImageUrl(sample.url);
                        setUploadedImagePreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                        imageUrl === sample.url
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={sample.url}
                        alt={sample.name}
                        className="w-full h-16 object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 64"><rect fill="%23f3f4f6" width="100" height="64"/><text fill="%239ca3af" x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="8">Error</text></svg>';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-xs font-medium px-1 text-center">{sample.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Image URL Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or enter Image URL
              </label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  setUploadedImagePreview(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                placeholder="https://example.com/image.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Model Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Model ({models.length} available)
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {models.map((model) => (
                  <button
                    key={model.id}
                    onClick={() => {
                      setSelectedModel(model.id);
                      if (model.supportsPrompt && model.defaultPrompt) {
                        setPrompt(model.defaultPrompt);
                      } else {
                        setPrompt('');
                      }
                    }}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      selectedModel === model.id
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900 text-sm">{model.name}</div>
                    <div className="text-xs text-gray-500 line-clamp-1">{model.description}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        model.type === 'style-transfer' ? 'bg-primary-100 text-primary-700' :
                        model.type === 'simple-cartoon' ? 'bg-green-100 text-green-700' :
                        model.type === 'flux-kontext' ? 'bg-orange-100 text-orange-700' :
                        model.type === 'flux-cartoon' ? 'bg-secondary-100 text-secondary-700' :
                        model.type === 'mirage-ghibli' ? 'bg-primary-100 text-primary-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {model.type}
                      </span>
                      <span className="text-xs text-gray-400">{model.cost}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt Input */}
            {currentModel.supportsPrompt && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt / Instructions
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={currentModel.defaultPrompt || 'Describe how to transform the image...'}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            )}

            {/* Preview */}
            {imageUrl && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200"><rect fill="%23f3f4f6" width="400" height="200"/><text fill="%239ca3af" x="50%" y="50%" text-anchor="middle" dy=".3em">Image failed to load</text></svg>';
                    }}
                  />
                </div>
              </div>
            )}

            {/* Process Button */}
            <button
              onClick={handleCartoonify}
              disabled={isProcessing || !imageUrl}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center space-x-2 ${
                isProcessing || !imageUrl
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                  : 'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white'
              }`}
            >
              {isProcessing ? (
                <>
                  <FiRefreshCw className="w-5 h-5 animate-spin" />
                  <span>Processing... (10-60s)</span>
                </>
              ) : (
                <>
                  <FiZap className="w-5 h-5" />
                  <span>Cartoonify with {currentModel.name || 'Selected Model'}</span>
                </>
              )}
            </button>
          </div>

          {/* Result Section */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Latest Result</h2>

            {result ? (
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <img
                    src={getImageUrl(result.cartoonUrl)}
                    alt="Cartoonified"
                    className="w-full object-contain"
                    style={{ maxHeight: '350px' }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-primary-50 rounded-lg p-3">
                    <div className="text-xs text-primary-600 font-medium flex items-center gap-1">
                      <FiClock className="w-3 h-3" /> Time
                    </div>
                    <div className="text-lg font-bold text-primary-900">{result.duration}s</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xs text-green-600 font-medium">Model</div>
                    <div className="text-sm font-bold text-green-900">{result.modelName}</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <div className="text-xs text-yellow-600 font-medium flex items-center gap-1">
                      <FiDollarSign className="w-3 h-3" /> Cost
                    </div>
                    <div className="text-lg font-bold text-yellow-900">{result.cost}</div>
                  </div>
                </div>

                {result.prompt && (
                  <div className="bg-primary-50 rounded-lg p-3">
                    <div className="text-xs text-blue-600 font-medium mb-1">Prompt Used</div>
                    <div className="text-sm text-blue-900">{result.prompt}</div>
                  </div>
                )}

                <a
                  href={result.remoteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 px-4 rounded-lg border border-primary-500 text-primary-600 hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <FiDownload className="w-4 h-4" />
                  <span>Download Full Resolution</span>
                </a>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg">
                <div className="text-center text-gray-400">
                  <FiImage className="w-12 h-12 mx-auto mb-2" />
                  <p>Cartoonified image will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch Process Tab */}
      {activeTab === 'batch' && (
        <div className="space-y-6">
          {/* Batch Controls */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Generate NFT Cards</h2>
                <p className="text-sm text-gray-500">AI cartoon + NFT card generated automatically</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  <span className="font-bold text-green-600">{completedCount}</span> done,{' '}
                  <span className="font-bold text-orange-600">{pendingCount}</span> pending
                </span>
                <button
                  onClick={loadBatchChallenges}
                  disabled={loadingChallenges}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <FiRefreshCw className={`w-4 h-4 ${loadingChallenges ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Batch Settings */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rarity</label>
                <select
                  value={batchRarity}
                  onChange={(e) => setBatchRarity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {rarities.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({rarityStats[r.id] || 0}/{batchChallenges.length})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <select
                  value={batchModel}
                  onChange={(e) => {
                    setBatchModel(e.target.value);
                    const model = models.find(m => m.id === e.target.value);
                    if (model?.defaultPrompt) {
                      setBatchPrompt(model.defaultPrompt);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.cost})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Batch Size</label>
                <select
                  value={batchLimit}
                  onChange={(e) => setBatchLimit(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value={1}>1 challenge</option>
                  <option value={5}>5 challenges</option>
                  <option value={10}>10 challenges</option>
                  <option value={25}>25 challenges</option>
                  <option value={50}>50 challenges</option>
                  <option value={100}>100 challenges</option>
                  <option value={0}>All pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt (editable)
                </label>
                <textarea
                  value={batchPrompt}
                  onChange={(e) => setBatchPrompt(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none text-sm"
                />
              </div>
            </div>

            {/* Batch Actions */}
            <div className="flex items-center gap-4">
              {batchStatus.status === 'running' ? (
                <>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>Processing... {batchStatus.completed || 0}/{batchStatus.total}</span>
                      <span>{batchStatus.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${batchStatus.progress || 0}%` }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleStopBatch}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <FiStopCircle className="w-4 h-4" />
                    Stop
                  </button>
                </>
              ) : (
                <button
                  onClick={handleStartBatch}
                  disabled={pendingForRarity === 0}
                  className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    pendingForRarity === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700'
                  }`}
                >
                  <FiPlay className="w-4 h-4" />
                  Start {batchRarity.charAt(0).toUpperCase() + batchRarity.slice(1)} ({pendingForRarity} pending)
                </button>
              )}
            </div>

            {batchStatus.status === 'completed' && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">
                  <FiCheck className="inline w-4 h-4 mr-1" />
                  Batch completed: {batchStatus.completed} success, {batchStatus.failed} failed
                </p>
              </div>
            )}
          </div>

          {/* Challenges Grid */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">All Challenges ({batchChallenges.length})</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Sort:</span>
                <button
                  onClick={() => handleSort('date')}
                  className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 transition-colors ${
                    sortBy === 'date'
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FiClock className="w-3.5 h-3.5" />
                  Date
                  {sortBy === 'date' && (
                    sortOrder === 'desc' ? <FiArrowDown className="w-3.5 h-3.5" /> : <FiArrowUp className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  onClick={() => handleSort('name')}
                  className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 transition-colors ${
                    sortBy === 'name'
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  A-Z
                  {sortBy === 'name' && (
                    sortOrder === 'asc' ? <FiArrowUp className="w-3.5 h-3.5" /> : <FiArrowDown className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>

            {loadingChallenges ? (
              <div className="text-center py-8 text-gray-500">
                <FiRefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                Loading challenges...
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {sortedBatchChallenges.map((challenge) => {
                  // Get image URL for selected rarity - NO fallback, show only the selected rarity
                  const selectedRarityData = challenge.rarityImages?.[batchRarity];
                  // Use thumbnail for grid view (faster loading)
                  const selectedRarityImage = selectedRarityData?.thumb || selectedRarityData?.url;
                  const hasAnyRarity = Object.keys(challenge.rarityImages || {}).length > 0;
                  const hasSelectedRarity = !!selectedRarityData;
                  // Prefer rarity-specific NFT card, fallback to main challenge NFT card for common rarity
                  const rarityNftCard = selectedRarityData?.nftCardUrl || selectedRarityData?.nftPreviewUrl
                    || (batchRarity === 'common' && challenge.nft_image_url ? challenge.nft_image_url : null);
                  const hasNftCard = !!rarityNftCard;
                  const displayImage = hasNftCard ? rarityNftCard : selectedRarityImage;

                  // Handler to open modal
                  const openModal = () => {
                    setRetryModal(challenge);
                    setRetryRarity(batchRarity);
                    setRetryModel(batchModel);
                    setRetryPrompt(rarityPrompts[batchRarity] || batchPrompt);
                  };

                  return (
                    <div
                      key={challenge.id}
                      className={`border rounded-lg overflow-hidden ${
                        hasNftCard ? 'border-green-500 ring-2 ring-green-200' : hasSelectedRarity ? 'border-green-300' : hasAnyRarity ? 'border-yellow-300' : 'border-gray-200'
                      }`}
                    >
                      {/* Images - clickable to open modal */}
                      <div
                        className="relative aspect-[4/5] bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={openModal}
                      >
                        {displayImage ? (
                          <img
                            src={getImageUrl(displayImage)}
                            alt={hasNftCard ? "NFT Card" : "Cartoon"}
                            className="w-full h-full object-cover"
                          />
                        ) : hasSelectedRarity ? (
                          // Has cartoon but no NFT card
                          <img
                            src={getImageUrl(selectedRarityImage)}
                            alt="Cartoon"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          // No image for this rarity - show original faded
                          <img
                            src={challenge.image_url}
                            alt="Original"
                            className="w-full h-full object-cover opacity-40"
                          />
                        )}
                        {/* Rarity badges */}
                        <div className="absolute top-1 left-1 flex gap-0.5">
                          {rarities.map((r) => (
                            challenge.rarityImages?.[r.id] && (
                              <span
                                key={r.id}
                                className={`w-4 h-4 rounded text-[10px] font-bold flex items-center justify-center ${
                                  r.color === 'gray' ? 'bg-gray-500 text-white' :
                                  r.color === 'blue' ? 'bg-secondary-500 text-white' :
                                  r.color === 'purple' ? 'bg-secondary-500 text-white' :
                                  'bg-yellow-400 text-yellow-900'
                                }`}
                                title={`${r.name} available`}
                              >
                                {r.name[0]}
                              </span>
                            )
                          ))}
                        </div>
                        {/* Status indicator - only show for missing rarities */}
                        {!hasSelectedRarity && (
                          <div className={`absolute top-1 right-1 px-1.5 py-0.5 text-white text-xs rounded ${
                            hasAnyRarity ? 'bg-yellow-500' : 'bg-orange-500'
                          }`}>
                            {hasAnyRarity ? `No ${batchRarity}` : 'Pending'}
                          </div>
                        )}
                      </div>

                      {/* Challenge info */}
                      <div className="p-2">
                        <h4
                          className="font-medium text-sm text-gray-900 truncate cursor-pointer hover:text-primary-600"
                          title={challenge.name}
                          onClick={openModal}
                        >
                          {challenge.name}
                        </h4>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">{challenge.type}</span>
                          {hasAnyRarity && (
                            <button
                              onClick={() => handleDeleteCartoon(challenge.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Remove all cartoons"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Results Gallery ({gallery.length} images)</h2>
            {gallery.length > 0 && (
              <button
                onClick={clearGallery}
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <FiTrash2 className="w-4 h-4" />
                Clear All
              </button>
            )}
          </div>

          {gallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((item) => (
                <div key={item.id} className="relative group">
                  <div
                    className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 cursor-pointer"
                    onClick={() => setEnlargedImage(item)}
                  >
                    <img
                      src={getImageUrl(item.cartoonUrl)}
                      alt={`${item.modelName} result`}
                      className="w-full h-32 object-cover hover:opacity-90 transition-opacity"
                    />
                    <div className="p-2 bg-white border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-900">{item.modelName}</span>
                        <span className="text-xs text-gray-500">{item.duration}s</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <a
                      href={getImageUrl(item.cartoonUrl)}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 bg-white rounded-full shadow-lg hover:bg-gray-100"
                    >
                      <FiDownload className="w-3 h-3 text-gray-600" />
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.saved) {
                          deleteFromServer(item);
                        } else {
                          removeFromGallery(item.id);
                        }
                      }}
                      className="p-1.5 bg-white rounded-full shadow-lg hover:bg-red-50"
                    >
                      <FiTrash2 className="w-3 h-3 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FiImage className="w-12 h-12 mx-auto mb-2" />
              <p>No images in gallery yet</p>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Auto-Generation Settings */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Auto-Generation Settings</h2>
            <p className="text-sm text-gray-600 mb-4">
              When enabled, AI cartoon and NFT card are automatically generated when a new challenge is created with an image.
            </p>

            {/* Auto-Generate Toggle */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <label className="font-medium text-gray-900">Auto-generate on challenge creation</label>
                <p className="text-sm text-gray-500">Automatically create cartoon image and NFT card</p>
              </div>
              <button
                onClick={() => updateSetting('autoGenerate', !settings.autoGenerate)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.autoGenerate ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.autoGenerate ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Default Model */}
            <div className="py-3 border-b">
              <label className="block font-medium text-gray-900 mb-2">Default AI Model</label>
              <p className="text-sm text-gray-500 mb-2">Model used for auto-generation when creating challenges</p>
              <select
                value={settings.defaultModel}
                onChange={(e) => updateSetting('defaultModel', e.target.value)}
                className="input max-w-md"
              >
                {availableModels.map((model) => (
                  <option key={model.key} value={model.key}>
                    {model.name} - {model.description}
                  </option>
                ))}
                {availableModels.length === 0 && models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="btn btn-primary flex items-center gap-2"
              >
                {savingSettings ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Rarity Prompts */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Rarity-Specific Prompts</h2>
            <p className="text-sm text-gray-600 mb-4">
              Custom prompts for each rarity level. These prompts are used when auto-generating cartoon images
              and during batch processing. Leave empty to use the model's default prompt.
            </p>

            <div className="space-y-4">
              {rarities.map((rarity) => {
                const selectedModel = availableModels.find(m => m.key === settings.defaultModel) ||
                                      models.find(m => m.id === settings.defaultModel);
                const supportsPrompt = selectedModel?.supportsPrompt;

                return (
                  <div key={rarity.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        rarity.id === 'common' ? 'bg-gray-100 text-gray-700' :
                        rarity.id === 'rare' ? 'bg-blue-100 text-blue-700' :
                        rarity.id === 'epic' ? 'bg-purple-100 text-purple-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {rarity.name}
                      </span>
                      {!supportsPrompt && (
                        <span className="text-xs text-gray-400">(Model doesn't use prompts)</span>
                      )}
                    </div>
                    <textarea
                      value={settings.rarityPrompts[rarity.id] || ''}
                      onChange={(e) => updateRarityPrompt(rarity.id, e.target.value)}
                      placeholder={supportsPrompt ? `Enter custom prompt for ${rarity.name} rarity...` : 'This model uses style transfer without prompts'}
                      disabled={!supportsPrompt}
                      rows={3}
                      className={`input w-full text-sm ${!supportsPrompt ? 'bg-gray-50 text-gray-400' : ''}`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="btn btn-primary flex items-center gap-2"
              >
                {savingSettings ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Save All Settings
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">How Auto-Generation Works</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>When you create a challenge with an image, the system automatically generates a cartoon version</li>
              <li>The cartoon image is created using the selected AI model and rarity-specific prompt</li>
              <li>The rarity is determined by the challenge difficulty (Easy=Common, Moderate=Rare, Hard=Epic, Expert=Legendary)</li>
              <li>An NFT card is also generated using the cartoon image</li>
              <li>This process runs in the background and doesn't block challenge creation</li>
            </ul>
          </div>
        </div>
      )}

      {/* Retry Modal */}
      {retryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {Object.keys(retryModal.rarityImages || {}).length > 0 ? 'Edit Rarities' : 'Process'}: {retryModal.name}
              </h3>
              <button onClick={() => setRetryModal(null)} className="p-1 hover:bg-gray-100 rounded">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Image comparison - Original vs NFT Card */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Original</label>
                  <img
                    src={retryModal.image_url}
                    alt="Original"
                    className="w-full h-64 object-cover rounded-lg border"
                  />
                </div>
                <div>
                  {(() => {
                    // Get the current rarity's data
                    const currentRarityData = retryModal.rarityImages?.[retryRarity];
                    const hasCartoon = !!currentRarityData?.url;
                    // Check for rarity-specific NFT card, fallback to main challenge NFT for common
                    const nftCardUrl = currentRarityData?.nftCardUrl
                      || (retryRarity === 'common' && retryModal.nft_image_url ? retryModal.nft_image_url : null);
                    const nftPreviewUrl = currentRarityData?.nftPreviewUrl
                      || (retryRarity === 'common' && retryModal.nft_preview_url ? retryModal.nft_preview_url : null);
                    const hasNftCard = !!nftCardUrl;
                    const rarityLabel = retryRarity.charAt(0).toUpperCase() + retryRarity.slice(1);

                    return (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {hasNftCard
                              ? `${rarityLabel} NFT Card`
                              : hasCartoon
                                ? `${rarityLabel} Cartoon`
                                : `${rarityLabel} Preview`}
                          </label>
                          {hasCartoon && (
                            <button
                              onClick={() => handleDeleteRarity(retryModal.id, retryRarity)}
                              className="text-red-600 hover:text-red-700 text-xs flex items-center gap-1"
                              title={`Delete ${retryRarity} image`}
                            >
                              <FiTrash2 className="w-3 h-3" />
                              Delete
                            </button>
                          )}
                        </div>
                        {/* Show rarity-specific NFT card if available, otherwise cartoon, otherwise empty state */}
                        {hasNftCard ? (
                          <img
                            key={`${retryModal.id}-${retryRarity}-nft`}
                            src={getImageUrl(nftPreviewUrl || nftCardUrl)}
                            alt={`${rarityLabel} NFT Card`}
                            className="w-full h-64 object-contain rounded-lg border cursor-pointer bg-gray-50"
                            onClick={() => window.open(getImageUrl(nftCardUrl), '_blank')}
                            title="Click to view full size NFT card"
                          />
                        ) : hasCartoon ? (
                          <img
                            key={`${retryModal.id}-${retryRarity}`}
                            src={getImageUrl(currentRarityData.thumb || currentRarityData.url)}
                            alt={`${rarityLabel} Cartoon`}
                            className="w-full h-64 object-cover rounded-lg border cursor-pointer"
                            onClick={() => window.open(getImageUrl(currentRarityData.url), '_blank')}
                            title="Click to view full size"
                          />
                        ) : (
                          <div className="w-full h-64 bg-gray-100 rounded-lg border flex flex-col items-center justify-center text-gray-400">
                            <FiImage className="w-12 h-12 mb-2 opacity-50" />
                            <p className="text-sm">No {rarityLabel.toLowerCase()} image yet</p>
                            <p className="text-xs mt-1">Generate one using the settings below</p>
                          </div>
                        )}
                        {hasNftCard && (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <FiCheck className="w-3 h-3" /> {rarityLabel} NFT card ready
                          </p>
                        )}
                        {hasCartoon && !hasNftCard && (
                          <p className="text-xs text-yellow-600 mt-1">
                            Cartoon ready, NFT card not yet generated
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Rarity selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Rarity</label>
                <div className="flex gap-2">
                  {rarities.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => {
                        setRetryRarity(r.id);
                        setRetryPrompt(getPromptForRarity(r.id));
                      }}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        retryRarity === r.id
                          ? r.color === 'gray' ? 'border-gray-500 bg-gray-100 text-gray-800' :
                            r.color === 'blue' ? 'border-blue-500 bg-blue-100 text-blue-800' :
                            r.color === 'purple' ? 'border-primary-500 bg-primary-100 text-primary-800' :
                            'border-yellow-500 bg-yellow-100 text-yellow-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {r.name}
                      {retryModal?.rarityImages?.[r.id]?.url && (
                        <FiCheck className="w-3 h-3 inline ml-1 text-green-500" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {retryModal?.rarityImages?.[retryRarity]?.url
                    ? `Will replace existing ${retryRarity} image`
                    : `Will create new ${retryRarity} image`}
                </p>
              </div>

              {/* Model selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <select
                  value={retryModel}
                  onChange={(e) => {
                    setRetryModel(e.target.value);
                    const model = models.find(m => m.id === e.target.value);
                    if (model?.defaultPrompt) {
                      setRetryPrompt(model.defaultPrompt);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} - {model.description} ({model.cost})
                    </option>
                  ))}
                </select>
              </div>

              {/* Prompt */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Prompt</label>
                  <button
                    onClick={saveRarityPrompt}
                    disabled={savingPrompt}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    title={`Save as default prompt for ${retryRarity}`}
                  >
                    {savingPrompt ? 'Saving...' : `Save as ${retryRarity} default`}
                  </button>
                </div>
                <textarea
                  value={retryPrompt}
                  onChange={(e) => setRetryPrompt(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                />
                {rarityPrompts[retryRarity] && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Custom prompt saved for {retryRarity}
                  </p>
                )}
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setRetryModal(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRetry}
                disabled={retrying}
                className="btn btn-primary flex items-center gap-2"
              >
                {retrying ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FiZap className="w-4 h-4" />
                    Process
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute top-2 right-2 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
            >
              <FiX className="w-6 h-6" />
            </button>

            <img
              src={getImageUrl(enlargedImage.cartoonUrl)}
              alt={`${enlargedImage.modelName} result`}
              className="max-w-full max-h-[80vh] object-contain"
            />

            <div className="p-4 bg-white border-t">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{enlargedImage.modelName || enlargedImage.modelKey}</h3>
                  <p className="text-sm text-gray-500">{enlargedImage.filename}</p>
                </div>
                <div className="flex items-center gap-4">
                  <a
                    href={getImageUrl(enlargedImage.cartoonUrl)}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary text-sm flex items-center gap-1"
                  >
                    <FiDownload className="w-4 h-4" />
                    Download
                  </a>
                  {enlargedImage.saved && (
                    <button
                      onClick={() => deleteFromServer(enlargedImage)}
                      className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-1"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AICartoonify;
